import type { OrganisationDto } from "@bafe/api-client";
import { Button, Card, Field, Input, Section, useToast } from "@bafe/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { ErrorPanel } from "../../components/ErrorPanel";
import { QueryState } from "../../components/QueryState";
import { api } from "../../lib/api";
import { fieldError } from "../../lib/errors";
import { formatDate } from "../../lib/format";
import { queryKeys } from "../../lib/query";

export function OrganisationSection() {
	const organisation = useQuery({
		queryKey: queryKeys.organisation,
		queryFn: () => api.getOrganisation(),
	});

	return (
		<QueryState query={organisation} errorTitle="We could not load your organisation.">
			{(data) => <OrganisationForm organisation={data} />}
		</QueryState>
	);
}

function OrganisationForm({ organisation }: { organisation: OrganisationDto }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const [name, setName] = useState(organisation.name);
	const [legalName, setLegalName] = useState(organisation.legalName ?? "");
	const [registrationNumber, setRegistrationNumber] = useState(organisation.registrationNumber ?? "");
	const [billingEmail, setBillingEmail] = useState(organisation.billingEmail);
	const [country, setCountry] = useState(organisation.country);
	const [timezone, setTimezone] = useState(organisation.timezone);

	const save = useMutation({
		mutationFn: () =>
			api.updateOrganisation({
				name: name.trim(),
				legalName: legalName.trim() === "" ? null : legalName.trim(),
				registrationNumber: registrationNumber.trim() === "" ? null : registrationNumber.trim(),
				billingEmail: billingEmail.trim(),
				country: country.trim(),
				timezone: timezone.trim(),
			}),
		onSuccess: (updated) => {
			queryClient.setQueryData(queryKeys.organisation, updated);
			void queryClient.invalidateQueries({ queryKey: queryKeys.session });
			toast({ tone: "success", message: "Organisation details saved." });
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		save.mutate();
	}

	return (
		<Section
			title="Organisation"
			description="Used on invoices and in the emails Key sends you about the service."
		>
			<form className="flex max-w-2xl flex-col gap-4" onSubmit={submit} noValidate>
				<Field label="Organisation name" error={fieldError(save.error, "name")} required>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							value={name}
							required
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setName(event.target.value)}
						/>
					)}
				</Field>

				<div className="grid gap-4 sm:grid-cols-2">
					<Field
						label="Registered legal name"
						help="Optional. Shown on invoices when it differs from the name above."
						error={fieldError(save.error, "legalName")}
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={legalName}
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setLegalName(event.target.value)}
							/>
						)}
					</Field>

					<Field
						label="Company registration number"
						help="Optional."
						error={fieldError(save.error, "registrationNumber")}
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={registrationNumber}
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setRegistrationNumber(event.target.value)}
							/>
						)}
					</Field>
				</div>

				<Field
					label="Billing email address"
					help="Invoices and payment notices go here."
					error={fieldError(save.error, "billingEmail")}
					required
				>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							type="email"
							value={billingEmail}
							required
							autoCapitalize="none"
							spellCheck={false}
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setBillingEmail(event.target.value)}
						/>
					)}
				</Field>

				<div className="grid gap-4 sm:grid-cols-2">
					<Field
						label="Country"
						help="Two-letter country code, such as MY."
						error={fieldError(save.error, "country")}
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={country}
								maxLength={2}
								autoCapitalize="characters"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setCountry(event.target.value.toUpperCase())}
							/>
						)}
					</Field>

					<Field
						label="Time zone"
						help="Used for dates shown in the control panel."
						error={fieldError(save.error, "timezone")}
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={timezone}
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setTimezone(event.target.value)}
							/>
						)}
					</Field>
				</div>

				{save.isError && <ErrorPanel error={save.error} title="We could not save these details." />}

				<div>
					<Button type="submit" variant="primary" pending={save.isPending} pendingLabel="Saving…">
						Save changes
					</Button>
				</div>
			</form>

			<Card className="flex flex-col gap-2">
				<p className="text-xs font-medium uppercase tracking-wide text-(--text-muted)">Account</p>
				<p className="text-sm text-(--text-secondary)">
					Organisation ID <span className="text-dns text-(--text-primary)">{organisation.id}</span>
				</p>
				<p className="text-sm text-(--text-secondary)">
					Created {formatDate(organisation.createdAt)}
				</p>
			</Card>
		</Section>
	);
}
